import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Download, Image, RefreshCw, Check, AlertCircle, 
  Database, ExternalLink, HardDrive, Zap
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const CMSImageLocalizationPage = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localizing, setLocalizing] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    loadPreview();
  }, []);

  const loadPreview = async () => {
    try {
      setLoading(true);
      const res = await api.get('/cms/localize-images/preview');
      setPreview(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const localizeImages = async () => {
    if (!window.confirm(`Télécharger et localiser ${preview?.total_external_images || 0} images externes ?`)) {
      return;
    }

    setLocalizing(true);
    try {
      const res = await api.post('/cms/localize-images');
      setResults(res.data);
      toast.success(`${res.data.images_localized} images localisées avec succès !`);
      loadPreview(); // Refresh preview
    } catch (error) {
      toast.error('Erreur lors de la localisation');
    } finally {
      setLocalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Localisation des Images</h1>
          <p className="text-slate-500">
            Téléchargez les images externes pour les stocker localement
          </p>
        </div>
        <Button variant="outline" onClick={loadPreview}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <ExternalLink className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{preview?.total_external_images || 0}</p>
                <p className="text-sm text-slate-500">Images externes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Database className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {Object.keys(preview?.by_collection || {}).length}
                </p>
                <p className="text-sm text-slate-500">Collections concernées</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <HardDrive className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {results?.images_localized || 0}
                </p>
                <p className="text-sm text-slate-500">Déjà localisées</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action Card */}
      <Card className="border-cyan-200 bg-gradient-to-r from-cyan-50 to-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-cyan-500" />
            Localiser les images externes
          </CardTitle>
          <CardDescription>
            Cette action va télécharger toutes les images hébergées sur des serveurs externes 
            (Unsplash, etc.) et les stocker localement dans /uploads/localized.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-white rounded-lg border space-y-2">
              <h4 className="font-medium">Avantages de la localisation :</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Indépendance vis-à-vis des serveurs externes
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Meilleure performance de chargement
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Pas de risque de liens cassés
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Contrôle total sur les assets
                </li>
              </ul>
            </div>

            <Button 
              onClick={localizeImages}
              disabled={localizing || (preview?.total_external_images || 0) === 0}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
              size="lg"
            >
              {localizing ? (
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Zap className="w-5 h-5 mr-2" />
              )}
              Localiser {preview?.total_external_images || 0} images
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-500" />
              Résultats de la localisation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3 mb-4">
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-700">{results.images_localized}</p>
                <p className="text-sm text-green-600">Localisées</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-700">{results.images_failed}</p>
                <p className="text-sm text-red-600">Échecs</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-700">{results.images_found}</p>
                <p className="text-sm text-blue-600">Total trouvées</p>
              </div>
            </div>

            {results.details?.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {results.details.slice(0, 20).map((detail, idx) => (
                  <div 
                    key={idx}
                    className={cn(
                      "flex items-center gap-3 p-2 rounded text-sm",
                      detail.status === 'success' ? "bg-green-50" : "bg-red-50"
                    )}
                  >
                    {detail.status === 'success' ? (
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    )}
                    <span className="text-slate-600">{detail.collection}</span>
                    <span className="text-slate-400">→</span>
                    <span className="text-slate-700 truncate flex-1">{detail.field}</span>
                  </div>
                ))}
                {results.details.length > 20 && (
                  <p className="text-sm text-slate-500 text-center py-2">
                    ... et {results.details.length - 20} autres
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview by Collection */}
      {preview?.by_collection && Object.keys(preview.by_collection).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-purple-500" />
              Images externes par collection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(preview.by_collection).map(([collection, images]) => (
                <div key={collection} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-slate-900">{collection}</h4>
                    <Badge variant="secondary">{images.length} images</Badge>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {images.map((img, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-slate-50 rounded">
                        <ExternalLink className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className="text-slate-600">{img.item_name}</span>
                        <span className="text-slate-400">•</span>
                        <span className="text-slate-500 truncate">{img.url}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CMSImageLocalizationPage;
